import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import useI18n from 'hooks/useI18n'
import { ReadObject } from 'services/storage'
import { CompleteAreaProfile, SelectEntryArea } from 'services/unifiedLogin'
import { IDENTITY_AREA_ROUTES, IDENTITY_AREA_SESSIONS } from 'services/identity'
import { profileNeedsCompletion, profileMissingFields } from 'services/profile'
import { DoLogout } from 'services/authentication'
import { exposeStrapiError } from 'utils'
import { industryOptions, optionsCountries } from 'utils/options'

export default function useController(area) {
  const { t } = useI18n()
  const history = useHistory()
  const formRef = useRef()
  const busy = useRef(false)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)
  const missingFields = useMemo(() => profileMissingFields(session?.user), [session])
  const initialValues = useMemo(() => Object.fromEntries(missingFields.map(field => [field, ''])), [missingFields])
  const formItems = useMemo(() => [
    { ref: 'firstName', label: t('first_name'), required: true, half: true },
    { ref: 'lastName', label: t('last_name'), required: true, half: true },
    { ref: 'country', label: t('profile_completion_country'), placeholder: t('profile_completion_country'), options: optionsCountries, required: true, full: true },
    { ref: 'companyName', label: t('profile_completion_company'), required: true, full: true },
    { ref: 'industry', label: t('industry'), placeholder: t('industry'), options: industryOptions, required: true, full: true },
    { ref: 'jobTitle', label: t('profile_completion_job_title'), required: true, full: true },
  ].filter(item => missingFields.includes(item.ref)).map(item => ({ ...item,
    full: missingFields.length === 1 || item.full, half: missingFields.length > 1 && item.half,
    id: `profile-completion-${item.ref}`, placeholder: item.label })), [t, missingFields])
  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        if (!ReadObject(IDENTITY_AREA_SESSIONS[area])?.jwt) { history.replace('/login'); return }
        const destination = await SelectEntryArea(area)
        if (!active) return
        const current = ReadObject(IDENTITY_AREA_SESSIONS[area])
        if (!destination) { setError(true); return }
        if (!profileNeedsCompletion(current?.user)) { history.replace(IDENTITY_AREA_ROUTES[area]); return }
        setSession(current)
      } catch { if (active) setError(true) }
      finally { if (active) setLoading(false) }
    }
    load()
    return () => { active = false }
  }, [area, history])
  const save = async () => {
    if (busy.current || !session) return
    const form = formRef.current?.getForm()
    if (!form) return
    busy.current = true
    setSaving(true)
    setError(false)
    try {
      const response = await CompleteAreaProfile(area, { ...form, expectedRevision: session.user.userProfile.revision })
      if (response && exposeStrapiError(response)) return
      if (response?.jwt) history.replace(IDENTITY_AREA_ROUTES[area])
      else setError(true)
    } catch { setError(true) }
    finally { busy.current = false; setSaving(false) }
  }
  const logout = async () => { await DoLogout(); history.replace('/login') }
  return { t, formRef, formItems, session, initialValues, loading, saving, error, save, logout }
}
